import React, { useEffect, useState } from 'react';
import { ArrowRight, Search, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface HeroProps {
  onOpenSampleModal?: () => void;
}
const heroSlides = [{
  src: 'https://cdn.magicpatterns.com/uploads/pLrVcnrbQiJtFoosE3AWT3/asset-labels.jpg',
  alt: 'Asset labels - barcode and identification labels'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/uH1CAGacPnUx7cXgA8uYKz/Nilpeter-FB-line.jpg',
  alt: 'Industrial label production'
}];
export function Hero({
  onOpenSampleModal
}: HeroProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  const goToSlide = (index: number) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  };
  const prevSlide = () => {
    setDirection(-1);
    setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
  };
  const nextSlide = () => {
    setDirection(1);
    setCurrentSlide(prev => (prev + 1) % heroSlides.length);
  };
  const handleSampleClick = (e: React.MouseEvent) => {
    if (onOpenSampleModal) {
      e.preventDefault();
      onOpenSampleModal();
    }
  };
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? '-100%' : '100%',
      opacity: 0
    })
  };
  return <section className="relative pt-[140px] md:pt-[150px] pb-14 bg-white overflow-hidden" data-id="element-293">
      {/* Subtle animated gradient background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-294">
        <motion.div className="absolute -top-1/2 -left-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-[#0052CC]/6 via-[#0052CC]/3 to-transparent blur-3xl" animate={{
        x: [0, 100, 0],
        y: [0, 50, 0],
        scale: [1, 1.1, 1]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-295" />
        <motion.div className="absolute -top-1/4 right-0 w-[600px] h-[600px] rounded-full bg-gradient-to-l from-slate-50 via-[#0052CC]/3 to-transparent blur-3xl" animate={{
        x: [0, -80, 0],
        y: [0, 80, 0],
        scale: [1, 1.15, 1]
      }} transition={{
        duration: 25,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 2
      }} data-id="element-296" />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-8 relative" data-id="element-297">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center" data-id="element-298">
          {/* Left Content */}
          <div className="lg:pl-4" data-id="element-299">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-300">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0A1628] leading-[1.08] mb-6" data-id="element-301">
                Industrial labels,
                <br data-id="element-302" />
                nameplates &
                <br data-id="element-303" />
                <span className="text-slate-400" data-id="element-304">safety signage</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-500 mb-6 max-w-md leading-relaxed" data-id="element-305">
                Durable identification products for manufacturing, facilities,
                and industrial environments.
              </p>

              {/* Search Bar */}
              <div className="mb-6 max-w-md" data-id="element-306">
                <div className="relative" data-id="element-307">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" data-id="element-308" />
                  <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search labels, materials, sizes…" className="w-full pl-14 pr-5 py-4 bg-white border border-slate-200 rounded-xl text-base placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/15 focus:border-[#0052CC] transition-all shadow-sm" data-id="element-309" />
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3" data-id="element-310">
                <a href="/products" className="px-8 py-4 bg-[#0052CC] text-white font-bold rounded-full hover:bg-[#0040A0] transition-all duration-300 flex items-center group text-base shadow-lg shadow-[#0052CC]/20 hover:shadow-xl hover:shadow-[#0052CC]/25 hover:-translate-y-0.5" data-id="element-311">
                  Browse products
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-312" />
                </a>
                <button onClick={handleSampleClick} className="px-6 py-4 bg-white border border-slate-200 text-[#0A1628] font-semibold rounded-full hover:border-[#0052CC] hover:text-[#0052CC] transition-all duration-300 text-base" data-id="element-313">
                  Request samples
                </button>
              </div>

              {/* Quick trust indicators */}
              <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-500" data-id="element-314">
                <span className="flex items-center gap-2" data-id="element-315">
                  <span className="h-2 w-2 rounded-full bg-green-500" data-id="element-316"></span>
                  In stock & ready to ship
                </span>
                <span className="flex items-center gap-2" data-id="element-317">
                  <span className="w-1 h-1 bg-slate-300 rounded-full" data-id="element-318"></span>
                  ISO 9001 certified
                </span>
              </div>
            </motion.div>
          </div>

          {/* Right - Image Slider */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="relative lg:-mr-8" data-id="element-319">
            <div className="relative aspect-[4/3] lg:aspect-[5/4]" data-id="element-320">
              <div className="absolute inset-0 rounded-2xl lg:rounded-l-2xl lg:rounded-r-none overflow-hidden bg-slate-100 shadow-2xl" data-id="element-321">
                <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-322">
                  <motion.img key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.6,
                  ease: 'easeInOut'
                }} src={heroSlides[currentSlide].src} alt={heroSlides[currentSlide].alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-323" />
                </AnimatePresence>

                {/* Slider Controls */}
                <div className="absolute inset-0 flex items-center justify-between px-4 pointer-events-none" data-id="element-324">
                  <button onClick={prevSlide} className="pointer-events-auto h-10 w-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all backdrop-blur-sm" aria-label="Previous slide" data-id="element-325">
                    <ChevronLeft className="h-5 w-5 text-[#0A1628]" data-id="element-326" />
                  </button>
                  <button onClick={nextSlide} className="pointer-events-auto h-10 w-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all backdrop-blur-sm" aria-label="Next slide" data-id="element-327">
                    <ChevronRightIcon className="h-5 w-5 text-[#0A1628]" data-id="element-328" />
                  </button>
                </div>

                {/* Dot Indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2" data-id="element-329">
                  {heroSlides.map((_, i) => <button key={i} onClick={() => goToSlide(i)} className={`h-2 rounded-full transition-all duration-300 ${i === currentSlide ? 'w-6 bg-white' : 'w-2 bg-white/50'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-330" />)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}